import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import { 
  Droplets, 
  Wrench, 
  ArrowLeft,
  Send,
  User,
  Building,
  Mail,
  Phone,
  Save,
  Loader2,
  CheckCircle2,
  Activity,
  Package,
  Shield,
  Beaker,
  FolderKanban,
  Truck,
  Users,
  Settings,
  Menu,
  Plus,
  Trash2,
  ShoppingCart,
  Brain
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Profile Storage Hook
const useProfile = () => {
  const [profile, setProfile] = useState({
    hospital_name: "",
    address: "",
    contact_name: "",
    contact_email: "",
    contact_phone: ""
  });

  useEffect(() => {
    const saved = localStorage.getItem("sterilemate_profile");
    if (saved) {
      try {
        setProfile(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load profile", e);
      }
    }
  }, []);

  const saveProfile = (newProfile) => {
    setProfile(newProfile);
    localStorage.setItem("sterilemate_profile", JSON.stringify(newProfile));
    toast.success("Profile saved successfully!");
  };

  return { profile, setProfile, saveProfile };
};

// Product Categories
const productCategories = [
  { value: "ro_systems", label: "RO Systems & Components", icon: "💧" },
  { value: "filters", label: "Filters & Membranes", icon: "🔬" },
  { value: "chemicals", label: "Chemicals & Treatments", icon: "⚗️" },
  { value: "testing", label: "Testing Equipment", icon: "📊" },
  { value: "consumables", label: "Consumables & Supplies", icon: "📦" },
  { value: "parts", label: "Replacement Parts", icon: "🔧" },
  { value: "other", label: "Other", icon: "📋" }
];

// Sidebar Navigation
const Sidebar = ({ isOpen, onClose }) => {
  const navItems = [
    { icon: Activity, label: "Dashboard", path: "/" },
    { icon: ShoppingCart, label: "Sourcing", path: "/hospital/backorders" },
    { icon: Wrench, label: "Maintenance", path: "/maintenance" },
    { icon: Package, label: "Orders", path: "/order-request", active: true },
    { icon: FolderKanban, label: "Projects", path: "/login" },
    { icon: Shield, label: "ST108 Compliance", path: "/st108" },
    { icon: Beaker, label: "Lab Testing", path: "/microbiology-testing" },
    { icon: Users, label: "Investor Portal", path: "/investor" },
    { icon: Settings, label: "Admin", path: "/admin" }
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 z-50 
        transform transition-transform duration-300 ease-in-out border-r border-slate-800/50
        lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="p-6 border-b border-slate-800/50">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30 relative">
              <Brain className="w-7 h-7 text-white" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
            </div>
            <div>
              <h1 className="text-white font-bold text-xl tracking-tight">AskIsaac</h1>
              <p className="text-slate-500 text-xs"></p>
            </div>
          </Link>
        </div>
        <nav className="p-4 space-y-1 mt-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group
                  ${item.active 
                    ? "bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-white border border-emerald-500/30" 
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                  }
                `}
              >
                <Icon className={`w-5 h-5 ${item.active ? "text-emerald-400" : "group-hover:text-emerald-400"}`} />
                <span className="font-medium">{item.label}</span>
                {item.active && <div className="ml-auto w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800/50 bg-slate-950/50">
          <div className="text-center mb-3">
            <p className="text-slate-500 text-xs">Part of the Sterile Mate Family</p>
          </div>
          <div className="flex items-center justify-center gap-4 text-slate-400 text-sm">
            <a href="tel:800-988-3892" className="flex items-center gap-1 hover:text-emerald-400 transition-colors">
              <Phone className="w-3 h-3" />
              <span className="text-xs">800.988.3892</span>
            </a>
            <a href="mailto:info@sterilemate.com" className="flex items-center gap-1 hover:text-emerald-400 transition-colors">
              <Mail className="w-3 h-3" />
              <span className="text-xs">Contact</span>
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

export default function OrderRequestPage() {
  const { profile, setProfile, saveProfile } = useProfile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [formData, setFormData] = useState({
    order_type: "quote",
    notes: "",
    urgency: "normal"
  });
  const [items, setItems] = useState([
    { product_name: "", category: "", quantity: 1, unit: "each", notes: "" }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const addItem = () => {
    setItems([...items, { product_name: "", category: "", quantity: 1, unit: "each", notes: "" }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.hospital_name || !profile.contact_email) {
      toast.error("Please save your facility profile first");
      setShowProfileModal(true);
      return;
    }

    const validItems = items.filter(item => item.product_name.trim());
    if (validItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        facility_name: profile.hospital_name,
        address: profile.address,
        contact_name: profile.contact_name,
        contact_email: profile.contact_email,
        contact_phone: profile.contact_phone,
        order_type: formData.order_type,
        items: validItems,
        notes: formData.notes,
        urgency: formData.urgency
      };

      await axios.post(`${API}/order-requests`, payload);
      toast.success("Order request submitted successfully!");
      setSubmitted(true);
      setFormData({ order_type: "quote", notes: "", urgency: "normal" });
      setItems([{ product_name: "", category: "", quantity: 1, unit: "each", notes: "" }]);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="lg:ml-64 flex items-center justify-center min-h-screen p-4">
          <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Order Submitted!</h2>
            <p className="text-slate-500 mb-6">
              We've received your order request and will prepare a quote shortly.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => setSubmitted(false)} className="bg-emerald-600 hover:bg-emerald-700">
                Submit Another Order
              </Button>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)} 
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Order Request</h1>
                <p className="text-sm text-slate-500">Request quotes or place supply orders</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_projectmate-6/artifacts/1cyzft3x_Sterile%20Mate%20Logo%20%282%29.jpg"
                alt="Sterile Mate"
                className="h-12 w-auto object-contain hidden sm:block"
              />
              <Button variant="outline" onClick={() => setShowProfileModal(true)} className="hidden sm:flex">
                <User className="w-4 h-4 mr-2" /> My Profile
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
          {/* Profile Summary */}
          {profile.hospital_name && (
            <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-4 mb-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{profile.hospital_name}</p>
                    <p className="text-sm text-slate-500">{profile.contact_name} • {profile.contact_email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProfileModal(true)}>
                  Edit
                </Button>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Type */}
            <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Order Type</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, order_type: "quote" })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.order_type === "quote"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <ShoppingCart className={`w-5 h-5 ${formData.order_type === "quote" ? "text-emerald-600" : "text-slate-400"}`} />
                    <span className={`font-medium ${formData.order_type === "quote" ? "text-emerald-700" : "text-slate-700"}`}>
                      Request Quote
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Get pricing before ordering</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, order_type: "order" })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.order_type === "order"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Package className={`w-5 h-5 ${formData.order_type === "order" ? "text-emerald-600" : "text-slate-400"}`} />
                    <span className={`font-medium ${formData.order_type === "order" ? "text-emerald-700" : "text-slate-700"}`}>
                      Place Order
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Order at current pricing</p>
                </button>
              </div>
            </div>

            {/* Products */}
            <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Products</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Product Name *</Label>
                          <Input
                            value={item.product_name}
                            onChange={(e) => updateItem(index, "product_name", e.target.value)}
                            placeholder="e.g., RO Membrane 4040"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Category</Label>
                          <Select 
                            value={item.category} 
                            onValueChange={(v) => updateItem(index, "category", v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {productCategories.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>
                                  {cat.icon} {cat.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2">
                          <div className="w-20">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                              className="mt-1"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs">Unit</Label>
                            <Select 
                              value={item.unit} 
                              onValueChange={(v) => updateItem(index, "unit", v)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="each">Each</SelectItem>
                                <SelectItem value="case">Case</SelectItem>
                                <SelectItem value="box">Box</SelectItem>
                                <SelectItem value="pack">Pack</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs">Notes (optional)</Label>
                      <Input
                        value={item.notes}
                        onChange={(e) => updateItem(index, "notes", e.target.value)}
                        placeholder="Part number, specifications, etc."
                        className="mt-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Additional Information</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Urgency</Label>
                  <Select 
                    value={formData.urgency} 
                    onValueChange={(v) => setFormData({ ...formData, urgency: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Flexible timing</SelectItem>
                      <SelectItem value="normal">Normal - Standard delivery</SelectItem>
                      <SelectItem value="high">High - Rush order</SelectItem>
                      <SelectItem value="urgent">Urgent - Critical need</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Shipping Address</Label>
                  <Input
                    value={profile.address}
                    readOnly
                    className="mt-1 bg-slate-50"
                    placeholder="Set in profile"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label>Order Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Special instructions, delivery requirements, etc."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between">
              <Link to="/">
                <Button type="button" variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/30"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit {formData.order_type === "quote" ? "Quote Request" : "Order"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </main>
      </div>

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Facility Profile</DialogTitle>
            <DialogDescription>
              Save your facility information for faster orders
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Hospital/Facility Name *</Label>
              <Input
                value={profile.hospital_name}
                onChange={(e) => setProfile({ ...profile, hospital_name: e.target.value })}
                placeholder="Memorial Hospital"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                placeholder="123 Medical Center Dr"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Name *</Label>
              <Input
                value={profile.contact_name}
                onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={profile.contact_email}
                onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                placeholder="john@hospital.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={profile.contact_phone}
                onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                placeholder="(800) 988-3892"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowProfileModal(false)}>Cancel</Button>
            <Button onClick={() => { saveProfile(profile); setShowProfileModal(false); }}>
              <Save className="w-4 h-4 mr-2" /> Save Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
